import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getRequest, postRequest } from '../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../context/global_context'
import DownloadInlines from '../vehicle/inline_connections/DownloadInlines'
import { downloadFileBlob } from '../../utils/common'
import { useVehicles } from '../../context/vehicle_context'
import {
  Form as AntDForm,
  Flex,
  List,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd'
import Button from '../common/Button'
import { CaretDownOutlined, CaretUpOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { UploadContext } from '../../context/upload_file_context'
import FileActions from './FileActions'
import { useIntl } from 'react-intl'
import { notifyError } from '../../utils/notifications'

export const ImporterRow = ({
  record,
  formRef,
  getImportFile,
  resetStations,
  downloadDTCMemo,
}) => {
  const intl = useIntl()
  const { dependencies, file_name, file_type, is_uploading, key, versions } =
    record
  const hasDependencies = dependencies !== ''
  const hasUploadedVersions = versions.length > 0
  const isInlineCsv = file_name === 'inlines.csv'
  const isDtcMemosCsv = file_name === 'dtc_memos.csv'

  const filenameClass = hasDependencies ? 'not-available' : 'available'
  const dependenciesClass = hasDependencies
    ? 'not-available'
    : hasUploadedVersions && !is_uploading
      ? 'available'
      : ''
  const dependenciesContent = hasDependencies
    ? dependencies
    : hasUploadedVersions && !is_uploading
      ? intl.formatMessage({ id: 'state.available' })
      : ''

  return (
    <Flex
      align="flex-start"
      gap={16}
      className={`importer-grid row ${hasDependencies || dependenciesContent == '' ? 'has-dependencies' : ''}`}
      data-testid="importer-row"
      data-file-type={key}
    >
      <div className={`file-column ${filenameClass}`}>{file_name}</div>

      <div className={`dependency-column ${dependenciesContent == '' ? 'hide' : ''}`}>
        <span className="status-wrapper">
          <span className={`status-icon ${dependenciesClass}`}></span>
          <span className={`status-text ${dependenciesClass}`} data-testid="importer-status">
            {dependenciesContent}
          </span>
        </span>
      </div>

      <div className={`actions-column ${hasDependencies ? 'hide' : ''}`}>
        {
          isDtcMemosCsv ? (
            <Button
              type="link"
              onClick={() => downloadDTCMemo()}
              className="link-button"
            >
              {intl.formatMessage({ id: 'forms.download' })}
            </Button>
          ) : (
            <>
              {
                isInlineCsv ? (
                  <DownloadInlines isButton={false} />
                ) : null
              }
              <FileActions
                fileId={file_name}
                filesList={versions}
                fileType={key}
                fileName={file_name}
                formRef={formRef}
                is_uploading={is_uploading}
                reloadData={() => {
                  getImportFile()
                }}
                resetStations={() => {
                  resetStations()
                }}
              />
            </>
          )
        }
      </div>
    </Flex>
  )
}

const FilesForm = ({}) => {
  const intl = useIntl()
  const interval = useRef(null)
  const formRef = createRef()
  const [value, dispatch] = useContext(NavContext)
  const [importFiles, setImportFiles] = useState([])
  const { selectedVehicleName, setHasBlockers } = useVehicles()
  const [form] = AntDForm.useForm()
  const [sortOrder, setSortOrder] = useState(null)
  const [uploadState] = useContext(UploadContext)
  const [modal, contextHolder] = Modal.useModal()

  let { vehicle } = useParams()

  useEffect(() => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'importers.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })
    getImportFile()

    return () => {
      closeInterval()
    }
  }, [])

  const closeInterval = () => {
    if (interval.current) {
      clearInterval(interval.current)
      interval.current = null
      setHasBlockers(false)
    }
  }

  const callFunctionInterval = () => {
    setHasBlockers(true)
    interval.current = setInterval(() => {
      getImportFile()
    }, 15000)
  }

  const getImportFile = async () => {
    let res = await getRequest(
      `/api/importer/import_types/?vehicle_id=${vehicle}`
    )

    // Everything below reads res.data.* several levels deep, so bail out before
    // it rather than throwing on a failed request. The loader is cleared at the
    // end of this function, so returning early has to clear it here - and a
    // 'loader' action resets `notification`, so notify after that, not before.
    if (res?.status === false) {
      dispatch({
        type: 'loader',
        data: { caption: '', showLoader: false },
        notification: {},
      })
      notifyError(dispatch, intl.formatMessage({ id: 'importers.loadError' }))
      return
    }

    setImportFiles([
      {
        dependencies: '',
        file_name: 'dtc_memos.csv',
        file_type: null,
        is_uploading: false,
        key: 'dtc_memos',
        versions: []
      },
      ...res.data.data
    ])

    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: intl.formatMessage({ id: 'layout.vehicleList' }) + ` / ${selectedVehicleName} / ` + intl.formatMessage({ id: 'importers.breadcrumbTitle' }),
        id: res.data.vehicle_data.id,
        show_back_btn: true,
        make_model_id: res.data.vehicle_data.make_model_id,
      },
    })

    if (res.data.is_upload_going_on) {
      if (!interval.current) {
        callFunctionInterval()
      }
    } else {
      closeInterval()
    }

    dispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })
  }

  const downloadDTCMemo = async () => {
    let res = await getRequest(`/api/dtc_memos/export/${vehicle}/`, true, true)
    // `res?.msg` was redundant: base.js sets status false on every failure, and
    // an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'importers.memoDownloadError' }),
        },
      })
    } else {
      downloadFileBlob(res, `${vehicle}_dtc_memo.csv`)
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'importers.downloadCompleted' }),
        },
      })
    }
  }

  const showResetStationsConfirm = () => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'importers.resetImports' }),
      width: '480px',
      content: (
        <>
          {intl.formatMessage({ id: 'importers.resetConfirm' })}
        </>
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.reset' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: resetStations,
    })
  }

  const resetStations = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'importers.stationReset' }),
        showLoader: true,
      },
      notification: {},
    })

    const res = await postRequest(`/api/stations/reset/${vehicle}/`)
    if (res?.status) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'importers.stationDeleteSuccess' }),
        },
      })
      getImportFile()
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'importers.resetStationsError' }),
        },
      })
    }
  }

  // Sort Importer data
  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const sortedData = useMemo(() => {
    if (!sortOrder) return importFiles
  
    return [...importFiles].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.dependencies.localeCompare(b.dependencies)
        : b.dependencies.localeCompare(a.dependencies)
    })
  }, [importFiles, sortOrder])

  // Reset other Select components on selection change
  useEffect(() => {
    if (!uploadState?.file_type) return
    
    // Antd throws a circular refernce warning if select component is updated individually, hence usign a single update 
    const resetFields = {}

    importFiles.forEach((file) => {
      if (file.key !== uploadState.file_type) {
        resetFields[file.key] = undefined
      }
    })

    form.setFieldsValue(resetFields)
  }, [uploadState, form])

  return (
    <>
      <div className="page edit-vehicle">
        <Row className="title">
          <Space size={'middle'}>
            <Typography.Title level={5}>{intl.formatMessage({ id: 'sidebar.importers' })}</Typography.Title>
          </Space>
        </Row>
        <Row className="body">
          <AntDForm
            id="vehicle-form-importers"
            name="vehicle-form-importers"
            form={form}
            layout="vertical"
          >
            <List
              bordered
              dataSource={sortedData}
              className="importers-list"
              header={
                <div className="importer-grid">
                  <div>{intl.formatMessage({ id: 'importers.name' })}</div>

                  <Flex
                    align="center"
                    gap={8}
                    className='sorting-column-header'
                    onClick={toggleSort}
                  >
                    <span>{intl.formatMessage({ id: 'importers.currentStatus' })}</span>
                    <div className="sort-icon">
                      <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''}/>
                      <CaretDownOutlined className={sortOrder === 'desc' ? 'active' : ''} />
                    </div>
                  </Flex>
                  
                  <div></div>
                </div>
              }
              renderItem={(record) => (
                <List.Item>
                  <ImporterRow
                    record={record}
                    formRef={form}
                    getImportFile={getImportFile}
                    resetStations={showResetStationsConfirm}
                    downloadDTCMemo={downloadDTCMemo}
                  />
                </List.Item>
              )}
            />
          </AntDForm>
        </Row>
      </div>

      {contextHolder}
    </>
  )
}

FilesForm.propTypes = {}

export default FilesForm
